/**
 * Design System - Switch Component
 *
 * Toggle switch for on/off states with smooth animations.
 *
 * @example
 * <Switch
 *   label="Enable notifications"
 *   checked={enabled}
 *   onChange={(e) => setEnabled(e.target.checked)}
 * />
 *
 * @example
 * <Switch
 *   label="Dark mode"
 *   description="Switch to dark theme"
 *   checked={darkMode}
 *   onChange={(e) => setDarkMode(e.target.checked)}
 *   size="lg"
 *   disabled
 * />
 */

import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';

const Switch = forwardRef(({
  label,
  description,
  checked = false,
  disabled = false,
  size = 'md',
  className,
  onChange,
  id,
  ...props
}, ref) => {
  // Generate unique ID if not provided
  const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;
  const descriptionId = description ? `${switchId}-description` : undefined;

  // Size styles
  const sizeStyles = {
    sm: {
      track: 'h-5 w-9',
      thumb: 'h-4 w-4',
      translate: 'translate-x-4',
      label: 'text-sm',
      description: 'text-xs',
    },
    md: {
      track: 'h-6 w-11',
      thumb: 'h-5 w-5',
      translate: 'translate-x-5',
      label: 'text-base',
      description: 'text-sm',
    },
    lg: {
      track: 'h-7 w-14',
      thumb: 'h-6 w-6',
      translate: 'translate-x-7',
      label: 'text-lg',
      description: 'text-base',
    },
  };

  // Track styles
  const trackStyles = cn(
    'relative inline-flex flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out cursor-pointer',
    'focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2',
    sizeStyles[size].track,
    checked ? 'bg-primary-600' : 'bg-gray-200',
    disabled && 'opacity-50 cursor-not-allowed'
  );

  // Thumb styles
  const thumbStyles = cn(
    'inline-block rounded-full bg-white shadow-lg transform transition-transform duration-200 ease-in-out',
    sizeStyles[size].thumb,
    checked ? sizeStyles[size].translate : 'translate-x-0.5'
  );

  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      {/* Label and description */}
      {(label || description) && (
        <div className="flex-1 min-w-0">
          {label && (
            <label
              htmlFor={switchId}
              className={cn(
                'block font-medium text-gray-900 cursor-pointer',
                sizeStyles[size].label,
                disabled && 'text-gray-500 cursor-not-allowed'
              )}
            >
              {label}
            </label>
          )}
          {description && (
            <p
              id={descriptionId}
              className={cn(
                'mt-0.5 text-gray-600',
                sizeStyles[size].description,
                disabled && 'text-gray-400'
              )}
            >
              {description}
            </p>
          )}
        </div>
      )}

      {/* Switch */}
      <div className={trackStyles}>
        {/* Hidden checkbox */}
        <input
          ref={ref}
          type="checkbox"
          id={switchId}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          aria-describedby={descriptionId}
          role="switch"
          aria-checked={checked}
          className="sr-only"
          {...props}
        />

        {/* Visual thumb */}
        <span
          aria-hidden="true"
          className={thumbStyles}
        />
      </div>
    </div>
  );
});

Switch.displayName = 'Switch';

Switch.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  onChange: PropTypes.func,
  id: PropTypes.string,
};

export default Switch;
